'use client';

import { useQuery } from 'convex-helpers/react/cache/hooks';
import { api } from 'convex/_generated/api';
import type { Id } from 'convex/_generated/dataModel';
import { useToggleSeedSown } from '@api/seedTracking';
import { findGardenById } from '@modules/my-gardens/business_logic';
import {
  buildMonthGrid,
  buildSowingCalendarEntries,
  buildWeekColumns,
  createHandleMarkSown,
  footerEntries,
  formatShortDateLabel,
  groupAgendaSections,
  monthLabel,
  parseLocalISODate,
  resolvePreferences,
  toLocalISODate,
  weekRangeLabel,
  weekSummary,
  type AgendaSection,
  type CalendarDayCell,
  type Garden,
  type SowingCalendarEntry,
  type SowingCalendarViewMode,
  type WeekDayColumn,
} from '@modules/sowing-calendar/business_logic';
import { useDashboardStore } from '@shared/store/useDashboardStore';
import { useSowingCalendarStore } from '@shared/store/useSowingCalendarStore';
import { ComponentType, useEffect, useMemo } from 'react';

export interface SowingCalendarProps {
  isLoading: boolean;
  gardens: Garden[];
  selectedGarden: Garden | null;
  selectedGardenId: Id<'gardens'> | null;
  viewMode: SowingCalendarViewMode;
  focusedDateLabel: string;
  todayDateLabel: string;
  weekRangeShort: string;
  agendaSections: AgendaSection[];
  monthCells: CalendarDayCell[];
  weekColumns: WeekDayColumn[];
  monthFooterEntries: SowingCalendarEntry[];
  weekFooterEntries: SowingCalendarEntry[];
  weekActionCount: number;
  weekSeedCount: number;
  onGardenChange: (id: Id<'gardens'> | null) => void;
  onViewModeChange: (mode: SowingCalendarViewMode) => void;
  onPrevPeriod: () => void;
  onNextPeriod: () => void;
  onMarkSown: (plantId: string, currentHasSown: boolean) => void;
}

interface SowingCalendarContainerProps {
  View: ComponentType<SowingCalendarProps>;
}

export function SowingCalendarContainer({ View }: SowingCalendarContainerProps) {
  const gardens = useQuery(api.gardens.queries.getUserGardens, {});
  const plants = useQuery(api.plants.queries.getAllPlants, {});
  const preferences = useQuery(api.userPreferences.queries.getUserPreferences, {});

  const selectedGardenId = useDashboardStore((s) => s.selectedGardenId);
  const setSelectedGardenId = useDashboardStore((s) => s.setSelectedGardenId);

  const viewMode = useSowingCalendarStore((s) => s.viewMode);
  const focusedDate = useSowingCalendarStore((s) => s.focusedDate);
  const setViewMode = useSowingCalendarStore((s) => s.setViewMode);
  const goToPrevPeriod = useSowingCalendarStore((s) => s.goToPrevPeriod);
  const goToNextPeriod = useSowingCalendarStore((s) => s.goToNextPeriod);

  useEffect(() => {
    if (!selectedGardenId && gardens && gardens.length > 0) {
      setSelectedGardenId(gardens[0]._id);
    }
  }, [selectedGardenId, gardens, setSelectedGardenId]);

  const selectedGarden = gardens !== undefined ? findGardenById(gardens, selectedGardenId) : null;

  const sowingConfigs = useQuery(
    api.plantSowingConfig.queries.getSowingConfigsByGarden,
    selectedGarden ? { gardenId: selectedGarden._id } : 'skip'
  );

  const plantDefaults = useQuery(api.userPlantDefaults.queries.getUserPlantDefaults, {});

  const seedTracking = useQuery(
    api.seedTracking.queries.getSeedTracking,
    selectedGarden ? { gardenId: selectedGarden._id } : 'skip'
  );

  const toggleSeedSown = useToggleSeedSown();
  const onMarkSown = createHandleMarkSown(selectedGarden, toggleSeedSown);

  const { hemisphere, region } = resolvePreferences(preferences);

  const today = useMemo(() => new Date(), []);
  const focused = useMemo(() => parseLocalISODate(focusedDate), [focusedDate]);

  const entries = useMemo(
    () =>
      buildSowingCalendarEntries({
        garden: selectedGarden,
        plants: plants ?? [],
        configs: sowingConfigs ?? [],
        seedTracking: seedTracking ?? [],
        hemisphere,
        region,
        plantDefaults: plantDefaults ?? [],
        today,
      }),
    [selectedGarden, plants, sowingConfigs, seedTracking, plantDefaults, hemisphere, region, today]
  );

  const agendaSections = useMemo(() => groupAgendaSections(entries), [entries]);
  const monthCells = useMemo(
    () => buildMonthGrid({ focusedDate: focused, today, entries }),
    [focused, today, entries]
  );
  const weekColumns = useMemo(
    () => buildWeekColumns({ focusedDate: focused, today, entries }),
    [focused, today, entries]
  );
  const todayIso = useMemo(() => toLocalISODate(today), [today]);
  const monthFooterEntries = useMemo(() => footerEntries(entries, ['out-of-season']), [entries]);
  const weekFooterEntries = useMemo(() => footerEntries(entries, ['out-of-season']), [entries]);
  const { actionCount: weekActionCount, seedCount: weekSeedCount } = weekSummary(entries);

  const focusedDateLabel =
    viewMode === 'week' ? weekRangeLabel(focusedDate) : monthLabel(focusedDate);
  const weekRangeShort = weekRangeLabel(todayIso);
  const todayDateLabel = formatShortDateLabel(todayIso);

  const isLoading =
    gardens === undefined ||
    plants === undefined ||
    preferences === undefined ||
    plantDefaults === undefined ||
    (selectedGarden !== null && (sowingConfigs === undefined || seedTracking === undefined));

  return (
    <View
      isLoading={isLoading}
      gardens={gardens ?? []}
      selectedGarden={selectedGarden}
      selectedGardenId={selectedGardenId}
      viewMode={viewMode}
      focusedDateLabel={focusedDateLabel}
      todayDateLabel={todayDateLabel}
      weekRangeShort={weekRangeShort}
      agendaSections={agendaSections}
      monthCells={monthCells}
      weekColumns={weekColumns}
      monthFooterEntries={monthFooterEntries}
      weekFooterEntries={weekFooterEntries}
      weekActionCount={weekActionCount}
      weekSeedCount={weekSeedCount}
      onGardenChange={setSelectedGardenId}
      onViewModeChange={setViewMode}
      onPrevPeriod={goToPrevPeriod}
      onNextPeriod={goToNextPeriod}
      onMarkSown={onMarkSown}
    />
  );
}
